import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdminApi from '../../Service/Admin.api';
import { toast } from 'react-toastify';

const ChatMigration = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleRunMigration = async () => {
    setLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const res = await AdminApi.createMissingSupportCases();
      if (res.success) {
        const msg = res.data?.message || "Migration completed successfully!";
        setSuccessMessage(msg);
        toast.success(msg);
      } else {
        const errorMsg = res.message || "Something went wrong during migration.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error("Migration error:", error);
      const errMsg = error?.response?.data?.message || error?.message || "Internal server error occurred.";
      setErrorMessage(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1c234b', textAlign: 'center' }}>
          Support Chat Migration
        </Typography>

        <Paper
          sx={{
            p: 4,
            borderRadius: 4,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
            border: '1px solid rgba(255, 255, 255, 0.18)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            textAlign: 'center'
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 4, lineHeight: 1.6 }}>
            This utility retroactively scans all registered Users, NRI Users, Corporate Users, and e-Mitra Users in the database.
            If any user is found without an active Admin Support chat room (Order Type 53), one will be created and automatically routed to an NLP executive.
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }} icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
              <AlertTitle>Success</AlertTitle>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left', borderRadius: 2 }}>
              <AlertTitle>Migration Failed</AlertTitle>
              {errorMessage}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleRunMigration}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            sx={{
              bgcolor: '#1c234b',
              '&:hover': {
                bgcolor: '#2d376b',
              },
              py: 1.5,
              px: 4,
              borderRadius: 3,
              fontSize: '1rem',
              fontWeight: 'bold',
              textTransform: 'none',
              boxShadow: '0 4px 14px 0 rgba(28, 35, 75, 0.3)',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {loading ? 'Running Migration...' : 'Start Migration'}
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default ChatMigration;
